import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MainLayoutComponent } from '../../../_layouts/main-layout/main-layout.component';
import { UserService } from '../../../_services/users.service';
import { StaffDialogComponent } from '../staff-dialog/staff-dialog.component';
import { RecordNavComponent, confirmLeave } from '../../../../Shared/_components/record-nav/record-nav.component';
import { StaffListState, StaffMember, staffMatches, toStaffMember } from '../staff.component';
import { staffRole } from '../staff-roles';

// A staff member on their own page: /staff/create, /staff/edit/:id. The form
// is the same component the dialog used, in page mode. Previous / Next follow
// the Staff list's filters.
@Component({
    selector: 'app-staff-edit',
    imports: [CommonModule, MainLayoutComponent, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, StaffDialogComponent, RecordNavComponent],
    templateUrl: './staff-edit.component.html'
})
export class StaffEditComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private userService = inject(UserService);

  @ViewChild(StaffDialogComponent) form?: StaffDialogComponent;

  userId = 0;
  listState: StaffListState = {};
  loading = true;
  notFound = false;
  member: StaffMember | null = null;
  formData: { member?: StaffMember } | null = null;
  nav: StaffMember[] = [];

  get isEdit(): boolean {
    return this.userId > 0;
  }

  ngOnInit(): void {
    this.userId = Number(this.route.snapshot.paramMap.get('id')) || 0;
    const qp = this.route.snapshot.queryParamMap;
    for (const k of ['q', 'role', 'status'] as const) {
      const v = qp.get(k);
      if (v !== null) this.listState[k] = v;
    }
    this.load();
  }

  private load(): void {
    this.userService.getStaff().subscribe({
      next: (rows) => {
        const all = rows.map(toStaffMember);
        this.nav = all.filter((m) => staffMatches(m, this.listState));
        if (this.isEdit) {
          this.member = all.find((m) => m.userid === this.userId) ?? null;
          this.notFound = !this.member;
          if (this.member) this.formData = { member: this.member };
        } else {
          this.formData = {};
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.notFound = true;
      },
    });
  }

  get position(): number {
    return this.nav.findIndex((m) => m.userid === this.userId);
  }

  get prev(): StaffMember | null {
    return this.position > 0 ? this.nav[this.position - 1] : null;
  }

  get next(): StaffMember | null {
    const i = this.position;
    return i >= 0 && i < this.nav.length - 1 ? this.nav[i + 1] : null;
  }

  name(m: StaffMember | null): string | null {
    return m ? `${m.firstname ?? ''} ${m.lastname ?? ''}`.trim() || m.useremail : null;
  }

  initials(m: StaffMember | null): string {
    if (!m) return '?';
    return ((m.firstname || m.useremail || '?')[0] + ((m.lastname || '')[0] || '')).toUpperCase();
  }

  badge(role: string): string {
    return staffRole(role)?.badgeClass ?? 'bg-neutral-100 text-neutral-600';
  }

  label(role: string): string {
    return staffRole(role)?.label ?? role;
  }

  private get dirty(): boolean {
    return !!this.form?.hasChanges;
  }

  goTo(m: StaffMember | null): void {
    if (!m) return;
    confirmLeave(this.dialog, this.dirty, this.name(this.member) ?? '', this.name(m)!).subscribe((ok) => {
      if (ok) this.router.navigate(['/staff/edit', m.userid], { queryParams: this.listState });
    });
  }

  backToList(force = false): void {
    const go = () => this.router.navigate(['/staff'], { queryParams: this.listState });
    if (force) return void go();
    confirmLeave(this.dialog, this.dirty, this.name(this.member) ?? 'this person', 'the list').subscribe((ok) => ok && go());
  }

  // Saved -> stay (edit) for Previous / Next, or back to the list (new); cancelled -> back.
  onDone(result: { status: boolean }): void {
    if (!result.status) return this.backToList();
    if (this.isEdit) this.load();
    else this.backToList(true);
  }
}
